import { Button } from "@mui/material";

import Page from "~/components/Page";
import TableControls from "~/components/TableControls";
import { useTrans } from '~/hooks/useTrans';
import { apiGetReports } from "~/providers/api";
import { IReportItem } from "~/providers/interfaces";
import ErrorInfo from '~/components/ErrorInfo';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import { DEFAULT_BUSSINESS_PLAN_TYPE_FILTER } from "~/const";
import BussinessPlanSelect from "~/components/BussinessPlanSelect";
import { projectsFilterStore } from "~/stores/projectsFilter";
import BillingMethodSelect from "~/components/BillingMethodSelect";
import { formatNumberToThousands } from "~/utils/utils";
import Loader from "~/components/Loader";
import BarGraph, { IBarGraphItem } from "~/components/Graph";

import "./style.less";

interface IGraphData {
	id: number;
	title: string;
	subTitle: string;
	columns: Array<string>;
	items: Array<IBarGraphItem>;
	getYAxe: (value: string) => string;
}

interface IState {
	loading: boolean;
	isDirty: boolean;
	reports: Array<IReportItem>;
	errorMsg: string;
	businessPlanType: string;
	billingMethod: string;
	graphs: Array<IGraphData>;
}

const ENABLE = true;
const THOUSAND = 1000;
const FILTER_FROM_DATE = {
	year: 2023,
	// srpna
	month: 8,
};

const INIT_STATE: IState = {
	loading: true,
	isDirty: false,
	reports: [],
	errorMsg: "",
	businessPlanType: DEFAULT_BUSSINESS_PLAN_TYPE_FILTER,
	billingMethod: null,
	graphs: [],
	...ENABLE
		? {}
		: {
			loading: false,
			errorMsg: "Výpis reportů je dočasně pozastavený",
		},
};

interface IGetGraphData {
	reports: Array<IReportItem>;
	title: string;
	subTitle?: string;
	isPrice?: boolean;
	thousandsDivider?: boolean;
	keys?: Array<string>;
	keyMatchPattern?: string | RegExp;
	graphLegendTrans: (value: keyof IReportItem) => string;
}

function getGraphData({ reports, keys = [], keyMatchPattern = "", title = "", isPrice, thousandsDivider, subTitle = "", graphLegendTrans }: IGetGraphData): IGraphData {
	const reversed = reports.slice().reverse();
	const columns: Array<string> = [];
	const allKeys = keys.length
		? keys
		: Object.keys(reports[0]).filter(key => key.match(keyMatchPattern));
	const items: Array<IBarGraphItem> = [];
	const objMapping: {[key: string]: IBarGraphItem; } = {};
	let maxValue = -Infinity;
	let getYAxe: IGraphData["getYAxe"] = null;

	for (const key of allKeys) {
		const newItem: IBarGraphItem = {
			label: graphLegendTrans(key),
			values: [],
		};

		items.push(newItem);
		objMapping[key] = newItem;
	}

	reversed.forEach(report => {
		const date = new Date(report.periodEnd);

		for (const key of allKeys) {
			let value = report[key] as number;

			if (isPrice) {
				/* eslint-disable-next-line */
				value = value / 100;
			}

			objMapping[key].values.push(value);
			maxValue = Math.max(maxValue, value);
		}

		columns.push(`${date.getMonth() + 1}. ${date.getFullYear()}`);
	});

	if (isPrice) {
		getYAxe = value => `${formatNumberToThousands(parseFloat(value))} Kč`;
	}

	if (thousandsDivider && maxValue > THOUSAND) {
		for (const item of items) {
			item.values = item.values.map(value => value / THOUSAND);
		}

		getYAxe = value => `${formatNumberToThousands(parseFloat(value))}k`;
	}

	return {
		id: Math.random(),
		title,
		subTitle,
		columns,
		items,
		getYAxe,
	};
}

function getAllGraphs(reportItems: Array<IReportItem>, graphLegendTrans: any) {
	return [
		getGraphData({
			title: "Částka za API",
			reports: reportItems,
			keys: ["amountTotal", "amountViaSos", "amountViaBarter", "amountViaWallet"],
			isPrice: true,
			graphLegendTrans,
		}),
		getGraphData({
			title: "Počet projektů - nové a aktivní",
			reports: reportItems,
			keys: ["newProjectCount", "projectsWithConsumption", "projectsBasicWithConsumption", "projectsAdvancedWithConsumption", "projectsVipWithConsumption"],
			thousandsDivider: true,
			graphLegendTrans,
		}),
		getGraphData({
			title: "Počet spotřebovaných kreditů",
			reports: reportItems,
			keys: ["totalConsumedCredits", "creditsFreeOfCharge", "creditsCharged"],
			thousandsDivider: true,
			graphLegendTrans,
		}),
		getGraphData({
			title: "Počet volání mapových dlaždic",
			subTitle: "Celkem a mapové dlaždice",
			reports: reportItems,
			keys: ["totalResourceCalls", "totalV1MaptilesCalls"],
			thousandsDivider: true,
			graphLegendTrans,
		}),
		getGraphData({
			title: "Počet volání veřejných služeb",
			subTitle: "Volání všech veřejných služeb",
			reports: reportItems,
			keyMatchPattern: /^totalV\d(?!Maptiles).*Calls$/u,
			thousandsDivider: true,
			graphLegendTrans,
		}),
		getGraphData({
			title: "Počet volání interních služeb",
			subTitle: "Volání všech interních služeb",
			reports: reportItems,
			keyMatchPattern: /^totalSznV.*Calls$/u,
			thousandsDivider: true,
			graphLegendTrans,
		}),
	].filter(item => item.items.length > 0);
}

export default function AdminGraphReportsPage() {
	const { trans, graphLegendTrans } = useTrans();
	const { lang } = useLang();

	function getInitState(): IState {
		const filtersState = projectsFilterStore.getState();

		return {
			...INIT_STATE,
			...filtersState.data.filter.businessPlanType
				? {
					businessPlanType: filtersState.data.filter.businessPlanType,
				}
				: {},
		};
	}

	const { state, updateState, setState } = myUseState<IState>(getInitState());

	function filterItems(items: Array<IReportItem>): Array<IReportItem> {
		return items.filter(item => {
			const date = new Date(item.periodStart);
			const year = date.getFullYear();
			const month = date.getMonth() + 1;

			return year > FILTER_FROM_DATE.year
				|| (FILTER_FROM_DATE.year === year && month >= FILTER_FROM_DATE.month);
		});
	}

	async function loadData() {
		updateState({
			loading: true,
			reports: [],
			graphs: [],
			errorMsg: "",
		});

		const reportsData = await apiGetReports(state.businessPlanType, state.billingMethod, lang);
		const reportItems = reportsData.noErrors
			? filterItems(reportsData.data)
			: [];
		const graphs = getAllGraphs(reportItems, graphLegendTrans);

		updateState({
			loading: false,
			...reportsData.noErrors
				? {
					reports: reportItems,
					graphs,
				}
				: {},
			errorMsg: reportsData.errors.join(", "),
		});
	}

	function updateFilterBusinessPlan(value: string | null) {
		const curGlobFilter = projectsFilterStore.getState().data.filter;
		const curFilter = {
			...state,
		};

		if (value === null) {
			if ("businessPlanType" in curFilter) {
				delete curFilter.businessPlanType;
			}

			if ("businessPlanType" in curGlobFilter) {
				delete curGlobFilter.businessPlanType;
			}
		} else {
			curFilter.businessPlanType = value;
			curGlobFilter.businessPlanType = value;
		}

		setState(curFilter);
		projectsFilterStore.getState().setFilter(curGlobFilter);
	}

	function updateBillingMethod(value: string | null) {
		const curFilter = {
			...state,
		};

		if (value === null) {
			if ("billingMethod" in curFilter) {
				delete curFilter.billingMethod;
			}
		} else {
			curFilter.billingMethod = value;
		}

		setState(curFilter);
	}

	function sendFilter() {
		loadData();
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized && ENABLE) {
			setState(getInitState());
			loadData();
		}
	}

	return <Page title={trans("menu.adminGraphReports")} breadcrumbs={[trans("administration"), trans("menu.adminGraphReports")]} checkInternalRole="salesman" onLoad={onPageLoad} isDirty={state.isDirty} seoTitleTrans="seo.title.admin.transactions">
		<TableControls className="adminGraphReports__tableControls">
			<div className="adminGraphReports__tableControlsLine">
				<BussinessPlanSelect value={state.businessPlanType} onChange={updateFilterBusinessPlan} />
				<BillingMethodSelect value={state.billingMethod} onChange={updateBillingMethod} />
				<Button color="success" variant="contained" onClick={sendFilter}>
					{ trans("filter") }
				</Button>
			</div>
		</TableControls>
		<div className="adminGraphReports__graphsHolder">
			{ state.graphs.length > 0 && state.graphs.map(graph => <BarGraph key={graph.id} columns={graph.columns} items={graph.items} title={graph.title} subTitle={graph.subTitle} getYAxe={graph.getYAxe} />) }
			{ (!state.loading && state.graphs.length === 0) && <ErrorInfo msg="Žádné grafy k vykreslení" className="margin-top-large" /> }
			{ state.loading && <Loader msg={trans("loadingData")} /> }
		</div>
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} className="margin-top-large" /> }
	</Page>;
}
